import {
  APPROVAL_MINE_ROLES,
  APPROVAL_QUEUE_ROLES,
  COMMISSION_LEADERBOARD_ROLES,
  COMMISSION_REPORT_VIEW_ROLES,
  COMMISSION_SETTLEMENT_ROLES,
  CUSTOMER_MANAGER_ROLES,
  CUSTOMER_READ_ROLES,
  INVITE_CODE_MANAGE_ROLES,
  INVITE_CODE_VIEW_ROLES,
  MODEL_PLAN_BIND_ROLES,
  MODEL_PLAN_MANAGE_ROLES,
  MODEL_PLAN_REBIND_ROLES,
  MODEL_PLAN_VIEW_ROLES,
  RECEIPT_FINANCE_DECISION_ROLES,
  RECEIPT_SUBMIT_ROLES,
  RECEIPT_VIEW_ROLES,
  ROLE_OPS_ADMIN,
  ROLE_OPS_CUSTOMER_MANAGER,
  ROLE_OPS_FINANCE_MANAGER,
  ROLE_OPS_RUNTIME_OPERATOR,
  ROLE_OPS_SALES_LEAD,
  ROLE_OPS_SUPER_ADMIN,
  ROLE_OPS_SUPPORT,
  ROLE_OPS_SUPPLY_DEMAND_MANAGER,
  SALES_COST_MANAGE_ROLES,
  SERVICE_LEVEL_ASSIGN_ROLES,
  SERVICE_LEVEL_MANAGE_ROLES,
  TENANT_ROUTING_ASSIGN_ROLES,
  expandOpsRoles,
  hasAnyOpsRole,
  type OpsPermissionRole,
} from '@nadoutong/contracts';

export {
  expandOpsRoles,
  hasAnyOpsRole,
  ROLE_OPS_ADMIN,
  ROLE_OPS_CUSTOMER_MANAGER,
  ROLE_OPS_FINANCE_MANAGER,
  ROLE_OPS_RUNTIME_OPERATOR,
  ROLE_OPS_SALES_LEAD,
  ROLE_OPS_SUPER_ADMIN,
  ROLE_OPS_SUPPORT,
  ROLE_OPS_SUPPLY_DEMAND_MANAGER,
};

export type OpsRole = OpsPermissionRole;

/**
 * Approval-center role sets (epic #1030 E1-S3, #1039). The queue is
 * for roles that can decide at least one approval kind; my-submissions
 * is any sales role (row-level requester scope enforced server-side).
 * Shared by the route guards, the nav items, and the shell chip.
 *
 * Since E1-S7 (#1043) these are re-exports of the contracts constants
 * DERIVED from `APPROVAL_KIND_APPROVER_ROLES` /
 * `APPROVAL_KIND_REQUESTER_ROLES` in `internal-approvals.ts`, so the
 * UI gates, the permissions matrix, and the kind registry cannot
 * drift — registering a new kind with a new approver role widens all
 * three at once.
 */
export { APPROVAL_MINE_ROLES, APPROVAL_QUEUE_ROLES };

/**
 * Receipt-console role sets (epic #1031 E2-R5, #1063) — re-exports of
 * the contracts constants that also drive `OPS_UI_ROUTE_PERMISSIONS`
 * and the ops-api `requireRoles` gates, so the nav item, the route
 * guard, and the matrix cannot drift:
 *   - view: admin + customer-manager (lead via expansion) +
 *     finance-manager; row scope is server-injected by ops-api.
 *   - submit: admin + customer-manager (CM owns, lead brand-wide).
 *   - decisions (credit / reject / void): finance-manager + admin only.
 */
export { RECEIPT_FINANCE_DECISION_ROLES, RECEIPT_SUBMIT_ROLES, RECEIPT_VIEW_ROLES };

/**
 * Sales-cost management role set (epic #1032 E3-P1, #1102) — re-export
 * of the contracts constant that also drives `OPS_UI_ROUTE_PERMISSIONS`
 * and the ops-api `requireRoles` gate: admin + sales lead ONLY. The
 * customer-manager role must never reach the lead pricing panel (it
 * exposes procurement cost).
 */
export { SALES_COST_MANAGE_ROLES };

/**
 * Commission settlement role set (epic #1034 E5-M4, #1146) — re-export of
 * the contracts constant that also drives `OPS_UI_ROUTE_PERMISSIONS` and
 * the ops-api `requireRoles` gate: finance-manager + admin ONLY. The
 * customer-manager and sales-lead roles never reach the settlement queue
 * (leads get the M5 leaderboard instead).
 */
export { COMMISSION_SETTLEMENT_ROLES };

/**
 * Commission + book-performance report role sets (epic #1034 E5-M5, #1147)
 * — re-exports of the contracts constants that also drive
 * `OPS_UI_ROUTE_PERMISSIONS` and the ops-api `requireRoles` gates:
 *   - VIEW (statements + book): admin + finance-manager + sales-lead +
 *     customer-manager. CM-reachable, procurement-free (banned-key sweep
 *     enforced).
 *   - LEADERBOARD (per-manager standings): admin + finance-manager +
 *     sales-lead. NOT customer-manager. Lives on its own /crm/leaderboard
 *     page so finance reaches it without the procurement-bearing pricing
 *     page.
 */
export { COMMISSION_REPORT_VIEW_ROLES, COMMISSION_LEADERBOARD_ROLES };

/**
 * Invite-code role sets (epic #1475) — re-exports of the contracts
 * constants that also drive `OPS_UI_ROUTE_PERMISSIONS` and the ops-api
 * `requireRoles` gates, so the nav item, the route guard, and the matrix
 * cannot drift:
 *   - VIEW (the page + the list read): admin + sales-lead +
 *     customer-manager. ops-api pins the row scope per tier — CM to its
 *     own codes, lead to its sales-profile brand.
 *   - MANAGE (mint / retire / re-enable / re-quota): admin + sales-lead
 *     ONLY. A CM reaching the page gets the read-only projection.
 * The activation settings card is narrower still (admin only) and gates
 * on `ROLE_OPS_ADMIN` directly — there is no exported set for it because
 * the matrix uses the shared `ADMIN_ROLES` constant.
 */
export { INVITE_CODE_MANAGE_ROLES, INVITE_CODE_VIEW_ROLES };

/**
 * Model-plan role sets (epic #2292 / #2294–#2297) — re-exports of the
 * contracts constants that also drive `OPS_UI_ROUTE_PERMISSIONS` and the
 * ops-api `requireRoles` gates, so the nav item, the App.tsx route guard,
 * and the matrix cannot drift:
 *   - VIEW (the page + list/detail/runs/unsynced reads): admin +
 *     sales-lead + customer-manager. ops-api pins brand for lead/CM.
 *   - MANAGE (create / edit / archive / clone / rollback): admin only.
 *   - BIND (attach a plan to an invite code): admin + sales-lead.
 *   - REBIND (org assign/detach and code-level restate): admin only.
 */
export {
  MODEL_PLAN_BIND_ROLES,
  MODEL_PLAN_MANAGE_ROLES,
  MODEL_PLAN_REBIND_ROLES,
  MODEL_PLAN_VIEW_ROLES,
};

/**
 * Customer READ / WRITE role sets (#1958) — re-exports of the contracts
 * constants that also drive the `/crm/customers/:orgId` matrix entry and the
 * ops-api gates on `/v1/ops/customers/*` + `/v1/ops/v2/customer-manager/*`:
 *
 *   - READ: admin + customer-manager (lead via expansion) + finance-manager,
 *     who signs stage 2 of a credit application and must be able to open the
 *     customer it is about. Guards ENTRY to the page, nothing else.
 *   - WRITE (`CUSTOMER_MANAGER_ROLES`): admin + customer-manager. This is the
 *     set the ops-api non-GET catch-alls are registered against, and the one
 *     Customer 360 computes `canWrite` from. A few controls on that page are
 *     narrower still (SALES_COST_MANAGE_ROLES for service suspension,
 *     SERVICE_LEVEL_ASSIGN_ROLES, TENANT_ROUTING_ASSIGN_ROLES, admin-only
 *     account actions) and keep their own gate on top; `canWrite` is the
 *     floor, not a replacement.
 */
export { CUSTOMER_MANAGER_ROLES, CUSTOMER_READ_ROLES };

/**
 * Service level role sets (#1723/#1724/#1725) — re-exports of the contracts
 * constants that also drive the six `OPS_API_ENDPOINT_PERMISSIONS` entries and
 * the ops-api gates, so the nav item, the App.tsx guard, the matrix, and the
 * server gate share ONE source:
 *
 *   - MANAGE (create / edit / archive a level) names the routing config the
 *     level anchors, so it is admin-only. It guards `/supply/service-levels`.
 *   - ASSIGN (read the catalogue, read/write one customer's level) is admin +
 *     customer-manager, against a projection carrying no routing-config
 *     identity. It guards the Customer 360 service-level tab.
 */
export { SERVICE_LEVEL_ASSIGN_ROLES, SERVICE_LEVEL_MANAGE_ROLES };

/**
 * Tenant-routing assignment (#1658 part A, narrowed to admin by #1724) —
 * re-export of the contracts constant that also drives the
 * `OPS_API_ENDPOINT_PERMISSIONS` entries and the router-wide ops-api gate on
 * `/v1/ops/tenant-routing`. Guards the Customer 360 "custom routes" tab
 * (#1785): list configs, one config's SKU summary, and the org assignment
 * switch. Read through the constant rather than spelled as admin, so the tab
 * and the endpoints it calls cannot disagree about who may open it.
 */
export { TENANT_ROUTING_ASSIGN_ROLES };

export function hasExplicitOpsRole(rawRoles: readonly string[], role: string): boolean {
  return rawRoles.includes(role);
}
